import { EDIT_SCOPE, EDIT_FIELD } from "./constants";

// Define fields that differ from UI friendly values during object comparison.
const courseFieldNames = {
	[EDIT_FIELD.NAME]: { display: "Title", mandatory: true },
	[EDIT_FIELD.DESCRIPTION]: { display: "Description", mandatory: true },
	[EDIT_FIELD.IMAGE_URL]: { display: "Image", mandatory: true }
};

const moduleFieldNames = {
	[EDIT_FIELD.NAME]: { display: "Title", mandatory: true },
	[EDIT_FIELD.DESCRIPTION]: { display: "Description", mandatory: true },
	DeviceTraining: { display: "Device Training" },
	ShowGuru: { display: "Show Guru" },
	Unlocked: { display: "Locked Learning Journey" },
	[EDIT_FIELD.ACTIVE]: { display: "Active" }
};

const submoduleFieldNames = {
	[EDIT_FIELD.NAME]: { display: "Title", mandatory: true },
	[EDIT_FIELD.DESCRIPTION]: { display: "Description", mandatory: true },
	[EDIT_FIELD.IMAGE_URL]: { display: "Device Image" },
	Keywords: { display: "Keywords" },
	Duration: { display: "Duration" },
	HasProgress: { display: "Has Progress" },
	StoredName: { display: "Content" }
};

function compareObjects(published, draft, fieldNames) {
	const changes = [];

	const isStringEmpty = (value) =>
		typeof value === "string" && value.trim() === "";

	if (!published) {
		for (const key of Object.keys(draft)) {
			if (!fieldNames[key] || !fieldNames[key].mandatory) {
				continue;
			}

			const newValue = draft[key];
			const label = fieldNames[key].display ?? key;

			if (typeof newValue === "string") {
				if (isStringEmpty(newValue)) {
					changes.push({ label: `${label} missing`, field: key });
				}
			}
		}
		return changes;
	}
	for (const key of Object.keys(draft)) {
		const oldValue = published?.[key];
		const newValue = draft[key];

		// Ignore any keys that are not in the fieldNames array.
		if (!fieldNames[key]) {
			continue;
		}

		if (typeof newValue !== "object" || newValue === null || oldValue === null) {
			if (oldValue !== newValue) {
				try {
					const label = fieldNames[key].display ?? `${key}`;
					changes.push({ label: `${label} updated`, field: `${key}` });
				}
				catch (error) {
					console.error(error, key);
				}
			}
		} 
	}
	return changes;
}

function positionChanged(published, draft) {
	if (!published) {
		return false;
	}
	for (const key of Object.keys(draft)) {
		const oldValue = published?.[key];
		const newValue = draft[key];

		if (key === EDIT_FIELD.POSITION && oldValue !== newValue) {
			return true;
		}
	}
	return false;
}

const buildCourseChanges = (published, draftData) => {
	let result = [];
	let courseChanges = compareObjects(published, draftData, courseFieldNames);
	if (courseChanges.length) {
		console.debug("Course changes", published, draftData);
		result.push({
			scope: EDIT_SCOPE.COURSE,
			id: draftData.Id,
			action: published ? "Edited" : "Added",
			name: published ? draftData.Name : "(New Course)",
			changes: courseChanges
		});
	}
	return result;
};

const buildModuleChange = (published, draftData, existingPositionChangeItem) => {
	let result = {
		changeList: null,
		position: null
	};
	let moduleChanges = compareObjects(published, draftData, moduleFieldNames);
	if (moduleChanges.length || !published) {
		console.debug("Module changes", published, draftData);
		result.changeList = {
			scope: EDIT_SCOPE.MODULE,
			id: draftData.Id,
			action: published ? "Edited" : "Added",
			name: published ? draftData.Name : "(New Module)",
			changes: moduleChanges
		};
	}
	if (positionChanged(published, draftData) && !existingPositionChangeItem) {
		result.position = {
			label: "Module positions updated",
			field: EDIT_FIELD.POSITION
		};
	}
	return result;
};

const buildSubmoduleChange = (published, draftData, parentId, existingPositionChangeItem) => {
	let result = {
		changeList: null,
		position: null
	};
	let submoduleChanges = compareObjects(published, draftData, submoduleFieldNames);
	if (submoduleChanges.length || !published) {
		console.debug("Submodule changes", published, draftData);
		result.changeList = {
			scope: EDIT_SCOPE.SUBMODULE,
			id: draftData.Id,
			parentId: parentId,
			action: published ? "Edited" : "Added",
			name: published ? draftData.Name : "(New Submodule)",
			changes: submoduleChanges
		};
	}
	if (positionChanged(published, draftData) && !existingPositionChangeItem) {
		result.position = {
			label: "Submodule positions updated",
			field: EDIT_FIELD.POSITION
		};
	}
	return result;
};

const handleDeletions = (published, drafts, scope, parentId) => {
	let result = {
		changeList: [],
		position: null
	};
	for (const item of published) {
		const draft = drafts?.find(m => m.Id === item.Id);
		if (!draft) {
			let change = {
				scope: scope,
				id: item.Id,
				action: "Deleted",
				name: item.Name,
				changes: []
			};
			if (parentId) {
				change.parentId = parentId;
			}
			result.changeList.push(change);
		}
	}
	return result;
};

function handlePositionChange(index, existing, scope, id, name, changeItem) {
	if (index === -1) {
		existing.push({
			scope: scope,
			id: id,
			action: "Edited",
			name: name,
			changes: [changeItem]
		});
	}
	else {
		const courseItem = existing[index];
		courseItem.changes.push({ label: `${changeItem.label} updated`, field: `${changeItem.field}` });
	}
	return existing;
}

function haveDeletionsOccurred(publishedItems, draftItems) {
	if (!Array.isArray(publishedItems) || !Array.isArray(draftItems)) {
		return false;
	}
	const publishedIds = publishedItems.map(x => x.Id);
	const draftIds = draftItems.map(x => x.Id);
	return publishedIds.some(id => !draftIds.includes(id));
}

export function buildChangeList(draftData, published) {
	let result = buildCourseChanges(published, draftData);

	let changeData;
	let modulePositionChangeItem = null;
	let submodulePositionChangeItem = {};
	for (const draftModule of draftData.Modules ?? []) {
		const publishedModule = published.Modules?.find(m => m.Id === draftModule.Id);
		changeData = buildModuleChange(publishedModule, draftModule, modulePositionChangeItem);
		if (changeData.changeList) {
			result.push(changeData.changeList);
		}
		if (changeData.position) {
			modulePositionChangeItem = changeData.position;
		}

		for (const draftSubmodule of draftModule.Submodules ?? []) {
			const publishedSubmodule = publishedModule?.Submodules?.find(m => m.Id === draftSubmodule.Id);
			changeData = buildSubmoduleChange(publishedSubmodule, draftSubmodule, draftModule.Id, submodulePositionChangeItem[draftModule.Id]);
			if (changeData.changeList) {
				result.push(changeData.changeList);
			}
			if (changeData.position) {
				submodulePositionChangeItem[draftModule.Id] = changeData.position;
			}
		}
		if (haveDeletionsOccurred(publishedModule?.Submodules ?? [], draftModule.Submodules ?? [])) {
			changeData = handleDeletions(publishedModule.Submodules, draftModule.Submodules, EDIT_SCOPE.SUBMODULE, draftModule.Id);
			if (changeData.changeList.length) {
				result = [...result, ...changeData.changeList];
			}
		}
	}
	if (draftData.Modules && haveDeletionsOccurred(published?.Modules ?? [], draftData.Modules)) {
		changeData = handleDeletions(published.Modules, draftData.Modules, EDIT_SCOPE.MODULE);
		if (changeData.changeList.length) {
			result = [...result, ...changeData.changeList];
		}
	}

	// Handle positioning.
	if (modulePositionChangeItem) {
		let index = result.findIndex(c => c.scope === EDIT_SCOPE.COURSE);
		handlePositionChange(index, result, EDIT_SCOPE.COURSE, draftData.Id, draftData.Name, modulePositionChangeItem);
	}
	for (const key of Object.keys(submodulePositionChangeItem)) {
		const moduleId = Number(key);
		let index = result.findIndex(c => c.scope === EDIT_SCOPE.MODULE && c.id === moduleId);
		const existingModule = (index === -1) ? draftData.Modules.find(x => x.Id === moduleId) : { Name: "" };
		handlePositionChange(index, result, EDIT_SCOPE.MODULE, moduleId, existingModule.Name, submodulePositionChangeItem[moduleId]);
	}
	return result;
}